import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { MWST_RATE_PERCENT, calcVatTotals } from "@/lib/vatConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDateDE(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

const pkgNet = (pkg) =>
  (pkg.line_items || []).reduce(
    (s, it) => s + Number(it.quantity || 0) * Number(it.unit_price_net || 0),
    0,
  );
// Brutto pro Position mit tatsächlicher tax_rate (7 % Privatkunden / 19 % B2B)
const pkgGross = (pkg) =>
  Math.round(
    (pkg.line_items || []).reduce((s, it) => {
      const net = Number(it.quantity || 0) * Number(it.unit_price_net || 0);
      return s + net * (1 + Number(it.tax_rate || MWST_RATE_PERCENT) / 100);
    }, 0) * 100
  ) / 100;
// Einheitliche MwSt-Rate falls alle Positionen gleich, sonst null (Text: "MwSt")
const unifiedRate = (items) => {
  const rates = new Set((items || []).map((it) => Math.round(Number(it.tax_rate || MWST_RATE_PERCENT))));
  return rates.size === 1 ? [...rates][0] : null;
};

function PositionsTable({ items }) {
  return (
    <div className="border border-[#E3DECE]">
      {items.map((it, idx) => {
        const rowNet = Number(it.quantity || 0) * Number(it.unit_price_net || 0);
        return (
          <div
            key={idx}
            className={`px-4 py-3 flex items-baseline justify-between ${idx > 0 ? "border-t border-[#E3DECE]" : ""}`}
          >
            <div className="flex-1 pr-4">
              <p className="text-sm font-medium">{it.name}</p>
              {it.description && (
                <p className="text-xs text-[#757067] mt-1">{it.description}</p>
              )}
              <p className="text-xs text-[#757067] mt-1">
                {Number(it.quantity).toString().replace(".", ",")} {it.unit_name} × {Number(it.unit_price_net).toFixed(2).replace(".", ",")} € netto
              </p>
            </div>
            <p className="font-mono text-sm whitespace-nowrap">{rowNet.toFixed(2).replace(".", ",")} €</p>
          </div>
        );
      })}
    </div>
  );
}

export default function AngebotAnnehmen() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [customerMessage, setCustomerMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null); // "accepted" | "declined" | null
  const [selectedPackageId, setSelectedPackageId] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/quotes/by-token/${token}`)
      .then(({ data }) => {
        setQuote(data);
        // Auto-preselect middle package as smart default (or first)
        if (data.has_packages && data.packages?.length > 0) {
          const middle = Math.floor(data.packages.length / 2);
          setSelectedPackageId(data.packages[middle].id);
        }
      })
      .catch((err) => setError(err.response?.data?.detail || "Angebot nicht gefunden"))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    if (quote?.has_packages && !selectedPackageId) {
      toast.error("Bitte ein Paket auswählen");
      return;
    }
    const selectedPkg = quote?.packages?.find((p) => p.id === selectedPackageId);
    const confirmMsg = quote?.has_packages
      ? `Paket „${selectedPkg?.name}" (${pkgGross(selectedPkg).toFixed(2).replace(".", ",")} € brutto) verbindlich buchen?`
      : "Angebot verbindlich annehmen?";
    if (!window.confirm(confirmMsg)) return;
    setSending(true);
    try {
      await axios.post(`${API}/quotes/by-token/${token}/accept`, {
        message: customerMessage,
        package_id: quote?.has_packages ? selectedPackageId : "",
      });
      setDone("accepted");
      toast.success("Angebot angenommen — Sie erhalten in Kürze die Auftragsbestätigung");
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  const decline = async () => {
    if (!window.confirm("Angebot ablehnen?")) return;
    setSending(true);
    try {
      await axios.post(`${API}/quotes/by-token/${token}/decline`);
      setDone("declined");
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#BA6D52]" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <XCircle className="w-12 h-12 text-red-700 mx-auto mb-4" />
        <h1 className="font-serif text-3xl">Angebot nicht verfügbar</h1>
        <p className="text-[#757067] mt-3">{error || "Der Link ist ungültig oder abgelaufen."}</p>
      </div>
    );
  }

  const isMulti = !!quote.has_packages;
  const selectedPkg = isMulti
    ? quote.packages?.find((p) => p.id === selectedPackageId)
    : null;

  // Berechnungen für Single-Angebot — pro Position mit tatsächlicher tax_rate
  const { netSum, mwstSum, grossSum } = calcVatTotals(quote.line_items, MWST_RATE_PERCENT);
  const singleRate = unifiedRate(quote.line_items);
  const singleRateLabel = singleRate !== null ? `${singleRate} % MwSt` : "MwSt";

  // Berechnungen für aktuell gewähltes Paket
  const selectedNet = selectedPkg ? pkgNet(selectedPkg) : 0;
  const selectedGross = selectedPkg ? pkgGross(selectedPkg) : 0;
  const selectedRate = selectedPkg ? unifiedRate(selectedPkg.line_items) : null;
  const selectedRateLabel = selectedRate !== null ? `${selectedRate} % MwSt` : "MwSt";

  const statusLabels = {
    sent: { icon: Clock, color: "text-blue-800", bg: "bg-blue-50", border: "border-blue-200", label: "Angebot wartet auf Ihre Rückmeldung" },
    accepted: { icon: CheckCircle2, color: "text-emerald-800", bg: "bg-emerald-50", border: "border-emerald-200", label: "Angebot bereits angenommen" },
    declined: { icon: XCircle, color: "text-red-800", bg: "bg-red-50", border: "border-red-200", label: "Angebot wurde abgelehnt" },
    expired: { icon: XCircle, color: "text-gray-700", bg: "bg-gray-50", border: "border-gray-200", label: "Angebot abgelaufen" },
    cancelled: { icon: XCircle, color: "text-gray-700", bg: "bg-gray-50", border: "border-gray-200", label: "Angebot storniert" },
    processing: { icon: Loader2, color: "text-gray-700", bg: "bg-gray-50", border: "border-gray-200", label: "Angebot wird erstellt …" },
  };
  const S = statusLabels[quote.status] || statusLabels.sent;
  const Icon = S.icon;
  const canRespond = quote.status === "sent" && !done;

  return (
    <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
      <p className="text-[10px] uppercase tracking-[0.35em] text-[#BA6D52]">
        {isMulti ? "Ihr persönliches Angebot" : "Unverbindliches Angebot"}
      </p>
      <h1 className="font-serif text-4xl md:text-5xl mt-3">
        {isMulti
          ? "Wählen Sie Ihr Menü."
          : `Angebot Nr. ${quote.quote_number || "—"}`}
      </h1>
      <p className="text-[#757067] mt-3">
        für <b>{quote.betrieb}</b>
        {quote.ansprechpartner ? ` · ${quote.ansprechpartner}` : ""} · gültig bis {formatDateDE(quote.valid_until)}
      </p>

      <div className={`mt-6 border ${S.border} ${S.bg} px-4 py-3 inline-flex items-center gap-2 text-sm ${S.color}`}>
        <Icon size={18} className={S.icon === Loader2 ? "animate-spin" : ""} />
        {done === "accepted" ? "✓ Vielen Dank — Angebot angenommen!" : done === "declined" ? "Angebot abgelehnt" : S.label}
      </div>

      <div className="mt-10 border border-[#E3DECE] p-6 md:p-8 bg-[#FAFAF7]">
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">Einsatz</p>
        {quote.custom_dates && quote.custom_dates.length > 0 ? (
          <div className="mt-2">
            <p className="text-lg"><b>{quote.custom_dates.length} Einsatztage</b></p>
            <ul className="mt-3 space-y-1 text-sm text-[#2C2A26]">
              {quote.custom_dates.map((cd, i) => (
                <li key={i} className="flex items-baseline gap-2">
                  <span className="text-[#BA6D52]">•</span>
                  <span>
                    {formatDateDE(cd.date)}
                    {(cd.von && cd.bis) ? ` · ${cd.von}–${cd.bis} Uhr` : cd.von ? ` · ab ${cd.von} Uhr` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-lg mt-2">
            {formatDateDE(quote.einsatzdatum)}
            {quote.anzahl_tage > 1 ? ` — ${quote.anzahl_tage} Tage` : ""}
            {quote.von_uhrzeit && quote.bis_uhrzeit ? ` · ${quote.von_uhrzeit} – ${quote.bis_uhrzeit} Uhr` : ""}
          </p>
        )}
        <p className="text-sm text-[#757067] mt-3">{quote.ort}</p>
        {quote.notes && (
          <p className="text-sm mt-3 whitespace-pre-line text-[#2C2A26]">{quote.notes}</p>
        )}
      </div>

      {/* MULTI-PACKAGE: Paket-Auswahl */}
      {isMulti ? (
        <div className="mt-10" data-testid="angebot-packages">
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] mb-3">
            {quote.packages.length} Pakete zur Auswahl
          </p>
          <p className="text-sm text-[#757067] mb-6">
            Vergleichen Sie die Optionen und wählen Sie das Paket aus, das am besten
            zu Ihrem Anlass passt.
          </p>
          <div className="space-y-4">
            {quote.packages.map((pkg) => {
              const isSelected = selectedPackageId === pkg.id;
              return (
                <label
                  key={pkg.id}
                  data-testid={`angebot-package-${pkg.id}`}
                  className={`block cursor-pointer transition-all ${
                    isSelected
                      ? "border-2 border-[#BA6D52] bg-[#FBF7EC] shadow-lg"
                      : "border border-[#E3DECE] bg-white hover:border-[#BA6D52]/40 hover:bg-[#FAFAF7]"
                  }`}
                >
                  <div className="p-5 md:p-6">
                    <div className="flex items-start gap-4">
                      <input
                        type="radio"
                        name="package"
                        checked={isSelected}
                        onChange={() => setSelectedPackageId(pkg.id)}
                        disabled={!canRespond}
                        className="mt-1.5 w-5 h-5 accent-[#BA6D52] cursor-pointer"
                        data-testid={`angebot-package-${pkg.id}-radio`}
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Paket</p>
                            <h3 className="font-serif text-2xl mt-1 text-[#2C2A26]">{pkg.name}</h3>
                            {pkg.description && (
                              <p className="text-sm text-[#5C574E] mt-2 italic leading-relaxed">
                                {pkg.description}
                              </p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Gesamt Brutto</p>
                            <p className="font-serif text-3xl text-[#BA6D52] mt-1">
                              {pkgGross(pkg).toFixed(2).replace(".", ",")} €
                            </p>
                            <p className="text-[10px] text-[#8A7A68]">inkl. {(() => { const r = unifiedRate(pkg.line_items); return r !== null ? `${r} % MwSt` : "MwSt"; })()}</p>
                          </div>
                        </div>
                        {isSelected && (
                          <div className="mt-5 pt-5 border-t border-[#BA6D52]/30">
                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067] mb-2">Positionen</p>
                            <PositionsTable items={pkg.line_items} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Zusammenfassung für aktuell gewähltes Paket */}
          {selectedPkg && (
            <div className="mt-8 border-2 border-[#BA6D52] bg-[#FBF7EC] p-5" data-testid="angebot-selected-summary">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Ihre Auswahl</p>
              <div className="mt-2 flex flex-col md:flex-row md:items-baseline md:justify-between gap-2">
                <p className="font-serif text-2xl">{selectedPkg.name}</p>
                <p className="font-serif text-3xl text-[#BA6D52]" data-testid="angebot-selected-gross">
                  {selectedGross.toFixed(2).replace(".", ",")} €
                </p>
              </div>
              <p className="text-xs text-[#757067] mt-1">
                Netto {selectedNet.toFixed(2).replace(".", ",")} € · zzgl. {selectedRateLabel}
              </p>
            </div>
          )}

          {quote.deposit_percent > 0 && selectedPkg && (
            <div className="mt-6 bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
              <p className="font-medium">
                Anzahlung: <b>{(selectedGross * quote.deposit_percent / 100).toFixed(2).replace(".", ",")} €</b> ({quote.deposit_percent} % vom Brutto)
              </p>
              <p className="text-xs mt-1">
                Fällig innerhalb {quote.deposit_due_days || 7} Tagen nach Ihrer Zusage.
              </p>
            </div>
          )}
        </div>
      ) : (
        // SINGLE: klassische Positionsliste
        <div className="mt-8">
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] mb-3">Positionen</p>
          <PositionsTable items={quote.line_items} />
          <div className="mt-4 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-[#757067]">Netto</span><span className="font-mono">{netSum.toFixed(2).replace(".", ",")} €</span></div>
            <div className="flex justify-between"><span className="text-[#757067]">zzgl. {singleRateLabel}</span><span className="font-mono">{mwstSum.toFixed(2).replace(".", ",")} €</span></div>
            <div className="flex justify-between border-t border-[#E3DECE] pt-2 mt-2 items-baseline">
              <span className="text-xs uppercase tracking-[0.2em] text-[#2C2A26]">Gesamt Brutto</span>
              <span className="font-serif text-3xl text-[#BA6D52]" data-testid="angebot-total-gross">{grossSum.toFixed(2).replace(".", ",")} €</span>
            </div>
          </div>
          {quote.deposit_percent > 0 && (
            <div className="mt-6 bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
              <p className="font-medium">
                Anzahlung: <b>{(grossSum * quote.deposit_percent / 100).toFixed(2).replace(".", ",")} €</b> ({quote.deposit_percent} % vom Brutto)
              </p>
              <p className="text-xs mt-1">
                Fällig innerhalb {quote.deposit_due_days || 7} Tagen nach Ihrer Zusage.
              </p>
            </div>
          )}
        </div>
      )}

      {canRespond && (
        <div className="mt-10 border-t border-[#E3DECE] pt-8">
          <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Anmerkung an Lukas (optional)</label>
          <textarea
            rows={3}
            value={customerMessage}
            onChange={(e) => setCustomerMessage(e.target.value)}
            placeholder="z. B. besondere Wünsche, Fragen zum Ablauf …"
            className="mt-2 w-full bg-[#FAFAF7] border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] p-3 text-sm font-light"
            data-testid="angebot-customer-message"
          />
          <div className="mt-6 p-4 bg-[#FAF5F1] border-l-4 border-[#BA6D52] text-sm font-light leading-relaxed text-[#2C2A26]"
            data-testid="angebot-storno-hinweis">
            <b className="text-[#BA6D52]">Terminreservierung und Stornierung:</b> Mit Ihrer Annahme
            wird der Termin exklusiv für Sie geblockt und nicht mehr anderweitig vergeben. Eine
            Stornierung ist bis <b>22&nbsp;Kalendertage</b> vor dem Termin kostenfrei, danach gilt
            eine gestaffelte Ausfallpauschale{" "}
            {quote.customer_type === "private"
              ? "(30 % / 50 % / 70 % / 90 % je nach Zeitpunkt, Ziffer 4.8 AGB)"
              : "(40 % / 60 % / 80 % / 90 % je nach Zeitpunkt, Ziffer 8 AGB)"}.
            Der Nachweis eines geringeren Schadens bleibt Ihnen vorbehalten; eine einmalige
            Terminverschiebung ist bis 21&nbsp;Tage vorher kostenfrei möglich.
          </div>
          <div className="mt-6 flex flex-col md:flex-row gap-3 justify-end">
            <button
              onClick={decline}
              disabled={sending}
              data-testid="angebot-decline"
              className="border border-[#E3DECE] hover:border-red-700 hover:text-red-700 px-6 py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-60"
            >
              Angebot ablehnen
            </button>
            <button
              onClick={accept}
              disabled={sending || (isMulti && !selectedPackageId)}
              data-testid="angebot-accept"
              className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-8 py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-60 inline-flex items-center gap-2"
            >
              {sending && <Loader2 size={14} className="animate-spin" />}
              {isMulti ? `Paket verbindlich buchen` : "Angebot verbindlich annehmen"}
            </button>
          </div>
          <p className="text-xs text-[#757067] mt-4 text-center">
            Mit Klick auf &bdquo;{isMulti ? "Paket verbindlich buchen" : "Angebot verbindlich annehmen"}&ldquo; bestätigen Sie die Buchung zu den oben genannten Konditionen.
          </p>
        </div>
      )}

      {done === "accepted" && (
        <div className="mt-10 text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-700 mx-auto mb-4" />
          <h2 className="font-serif text-2xl">Vielen Dank!</h2>
          <p className="text-[#757067] mt-3 max-w-lg mx-auto">
            Ihr Auftrag wurde erfasst. Sie erhalten in Kürze die verbindliche Auftragsbestätigung per E-Mail.
          </p>
        </div>
      )}
    </div>
  );
}
